import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import React, { forwardRef, useCallback, useRef, useState } from 'react';
import { usePrerequisites } from '../../../contexts/Prerequisites';
import { useDidUpdate } from '../../../hooks';

const PasswordAdornment = ({ onClick, disabled, readOnly, passwordVisible }) => (
	<InputAdornment position="end">
		<IconButton aria-label="toggle password visibility" onClick={onClick} disabled={disabled || readOnly} edge="end">
			{passwordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
		</IconButton>
	</InputAdornment>
);

const TextInput = forwardRef(({
	eager,
	label,
	type = 'text',
	readOnly,
	disabled,
	required,
	error,
	onBlur,
	multiline,
	rows,
	helperText,
	onChange,
	value,
	onClearError,
	trimWhitespaces = true,
	...rest
}, ref) => {
	const inputRef = useRef();
	const hasFocusRef = useRef(false);
	const [{ strings }] = usePrerequisites();
	const [state, setState] = useState({ passwordVisible: false });
	const handleToggleShowPassword = useCallback(() => {
		setState((prev) => ({ passwordVisible: !prev.passwordVisible }));
	}, [type]);
	const applyValueChangeEvent = useCallback(
		(event, trimValue = false) => {
			event?.persist?.();
			let val = event?.target?.value;
			if (type === 'number') {
				val = val = event?.target?.valueAsNumber;
				if (isNaN(val)) {
					val = value;
				}
			} else {
				const valueNeedsTrim = type === 'text' && typeof val === 'string' && /(^\s)|(\s$)/.test(val);
				if (valueNeedsTrim && trimValue) {
					val = valueNeedsTrim ? val.trim() : val;
					inputRef.current.value = val;
				}
			}
			if (val !== value && typeof onChange === 'function') {
				onChange(event, val);
			}
		},
		[error, value, eager, onChange, trimWhitespaces]
	);



	const handleChange = useCallback(
		(event) => {
			event.persist();

			if (!!error && !eager && typeof onClearError === 'function') {
				onClearError(event); // This will clear validation error on change
			}
			if (!hasFocusRef.current || eager) {
				applyValueChangeEvent(event);
			}
		},
		[applyValueChangeEvent, onClearError]
	);

	const handleFocus = () => {
		hasFocusRef.current = true;
	};

	const handleBlur = useCallback(
		async (event) => {
			event.persist();
			let val = event?.target?.value;
			if (type === 'number') {
				val = val = event?.target?.valueAsNumber;
				if (isNaN(val)) {
					val = undefined;
				}
			}
			hasFocusRef.current = false;
			const valueNeedsTrim = type === 'text' && typeof val === 'string' && /(^\s)|(\s$)/.test(val);
			val = valueNeedsTrim ? val.trim() : val;
			if ((valueNeedsTrim || !eager) && val !== value) {
				applyValueChangeEvent(event, trimWhitespaces);
			}
			if (typeof onBlur === 'function') {
				onBlur(event);
			}
		},
		[type, value, applyValueChangeEvent, trimWhitespaces]
	);

	const setValue = (val) => {
		if (inputRef.current && inputRef.current.value !== val) {
			inputRef.current.value = val; //Provision for resetting value
		}
	};

	useDidUpdate(() => {
		setValue(value);
	}, [value]);

	return (
		<TextField
			type={state.passwordVisible ? 'text' : type || 'text'}
			size="small"
			margin="dense"
			variant="outlined"
			label={strings[label] || label}
			required={required}
			multiline={multiline}
			rows={multiline ? (rows > 0 ? rows : 4) : undefined}
			fullWidth
			defaultValue={value}
			disabled={disabled}
			readOnly={readOnly}
			onChange={handleChange}
			onBlur={handleBlur}
			onFocus={handleFocus}
			helperText={error || helperText}
			error={Boolean(error)}
			inputRef={inputRef}
			autoComplete="off"
			ref={ref}
			tabIndex={readOnly || disabled ? -1 : undefined}
			InputLabelProps={value?.length > 0 ? { shrink: true } : {}}
			{...rest}
			InputProps={{
				endAdornment: type === 'password' && (
					<PasswordAdornment
						passwordVisible={state.passwordVisible}
						onClick={handleToggleShowPassword}
						position="end"
						readOnly={readOnly}
						disabled={disabled}
					/>
				),
				...rest?.InputProps
			}}
		/>
	);
});

export default TextInput;